import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Button} from 'react-bootstrap';
import * as R from 'ramda';
import t from 'tcomb-form';
import moment from 'moment';
import classNames from 'classnames';
import {Modal} from 'react-bootstrap';

import {track} from 'js/common/analytics';
import * as ObjectHelper from 'js/common/object_helper';
import {ENUMS, MAPS} from 'js/common';
import IconLike from 'images/icons/svg/icon-thumbsup-white-blue-bg.svg';
import * as PostProps from 'js/common/proptypes/post_props';
import StarRating from 'js/components/star_rating';
import {FormLabel} from 'js/form_components';
import LoadingSpinner from 'js/components/loading_spinner';
import CutoutPreview from '../ad_mode/cutout_preview';
import OverlayPreview from '../ad_mode/overlay_preview';
import SandwichPreview from '../ad_mode/sandwich_preview';
import FacebookPagesShareButton from '../share_buttons/facebook_pages_share_button';
import AdModeInfo from '../ad_mode/ad_mode_info';
import {FEATURE_FLAGS} from 'js/common';
import ContentModal from '../content_modal/index';
import ContentDownloadSurvey from '../content_download_survey';
import ContentDownloadSurveyConfirmation from '../content_download_survey_confirmation';
import DistributionButtons from '../distribution_buttons';
import PinterestAdModeButton from '../content_modal/ad_mode_buttons/pinterest';
import 'scss/campaign_analytics_v2_modals.scss';

const {Form} = t.form;
const {isAgency, isBrand} = window.sn;

/**
 * Note: the body of the class, other than the render method, was copied from
 * public/js/campaign_analytics/v2/campaign_analytics_page/posts/content_modal/index.js
 * It was previously extending that class component, which was causing errors
 * after upgrading to react 16.
 */
export default class ImageModal extends Component {

    static propTypes = {
        close: PropTypes.func.isRequired,
        isAdmin: PropTypes.bool.isRequired,
        setPostScore: PropTypes.func.isRequired,
        post: PropTypes.shape(PostProps.POST),
        brandName: PropTypes.string,
        campaignName: PropTypes.string,
        campaignId: PropTypes.number,
        email: PropTypes.string,
        downloadUrl: PropTypes.string,
        handleDownloadContent: PropTypes.func,
        shareToFacebookAdManager: PropTypes.func,
        shareToFacebookPagesAccount: PropTypes.func,
        shareToTwitterAdManager: PropTypes.func,
        shareToSnapchatAdManager: PropTypes.func,
        shareToPinterest: PropTypes.func,
        createPinterestBoard: PropTypes.func,
        isSalesUser: PropTypes.bool,
        showDownloadSurvey: PropTypes.bool,
        showPostScore: PropTypes.bool,
        showAvgScore: PropTypes.bool,
        previewAdMode: PropTypes.bool,
        shareOptions: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
        showContentComment: PropTypes.bool,
        postUserComment: PropTypes.string,
        campaignType: PropTypes.bool
    };

    constructor(props) {
        super(props);

        this.state = {
            uploading: false,
            showFacebookPagesSharingForm: false,
            adMode: !!props.previewAdMode,
            platform: 'facebook',
            format: 'mobile',
            composingContentComment: props.showContentComment,
            postUserComment: null
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const currPreviewAdMode = this.props.previewAdMode;
        const nextPreviewAdMode = nextProps.previewAdMode;

        const isBeginningComposingContentComment =
            !this.state.composingContentComment && nextProps.showContentComment;

        const isFinishedComposingContentComment =
            this.state.composingContentComment && !nextProps.showContentComment;

        const isGettingNewContentComment =
            R.not(R.equals(this.props.postUserComment, nextProps.postUserComment)) &&
            !R.isNil(this.state.postUserComment);

        const isGettingCurrentContentComment =
            R.not(R.equals(this.props.postUserComment, nextProps.postUserComment)) &&
            R.isNil(this.state.postUserComment) &&
            !R.isNil(nextProps.postUserComment);

        if (R.not(R.equals(currPreviewAdMode, nextPreviewAdMode))) {
            this.setState({
                adMode: nextPreviewAdMode
            });
        }

        if (isBeginningComposingContentComment) {
            this.setState({
                composingContentComment: true
            });
        }

        if (isFinishedComposingContentComment) {
            this.setState({
                composingContentComment: false
            });
        }

        if (isGettingNewContentComment || isGettingCurrentContentComment) {
            this.setState({
                postUserComment: nextProps.postUserComment
            });
        }
    }

    static activePlatforms = R.ifElse(
        R.always(FEATURE_FLAGS.isPinterestEnabled),
        R.append('pinterest'),
        R.identity
    )(['facebook', 'instagram', 'twitter']);

    displayModalBodyContent = () => {
        const {post} = this.props;
        const {adMode} = this.state;

        if (adMode) {
            return this.displayAdModeContent();
        }

        return <img src={post.content_url} role="presentation" />;
    };

    displayModalBodyInfo = () => {
        const {close, showDownloadSurvey, showDownloadSurveyThankYou} = this.props;

        const {composingContentComment, adMode} = this.state;

        if (adMode) {
            return this.displayAdModeInfo();
        }

        const downloadContentUrl = this.getDownloadContentUrl();
        const fileName = this.getDownloadFileName();

        const sharingForm = this.displayFacebookPageSharingForm();

        if (!R.isNil(sharingForm)) {
            return sharingForm;
        }

        if (showDownloadSurvey) {
            return (
                <ContentDownloadSurvey
                    brandName={this.props.brandName}
                    campaignName={this.props.campaignName}
                    downloadContentUrl={downloadContentUrl}
                    fileName={fileName}
                    post={this.props.post}
                    handleSubmitContentDownloadSurvey={this.props.handleSubmitContentDownloadSurvey}
                    close={close}
                />
            );
        }

        if (showDownloadSurveyThankYou) {
            return (
                <ContentDownloadSurveyConfirmation
                    downloadContentUrl={downloadContentUrl}
                    fileName={fileName}
                />
            );
        }

        if (composingContentComment) {
            return this.displayCommentComposer();
        }

        const creatorInfoSection = this.displayCreatorInfoSection();
        const postScore = this.displayPostScore();
        const downloadButton = this.displayDownloadButton();

        return (
            <div>
                {creatorInfoSection}

                <div className="rating-download">
                    {postScore}
                    {downloadButton}
                    <DistributionButtons campaignId={this.props.post.campaign_id} />
                </div>
            </div>
        );
    };

    getDownloadContentUrl = () => {
        const {post, downloadUrl} = this.props;

        return R.pipe(
            R.replace('__CAMPAIGN_ID__', post.campaign_id),
            R.replace('__POST_ID__', post.post_id),
            R.replace('__PLATFORM_TYPE__', post.platform_type),
            R.ifElse(R.equals('#'), R.always(post.content_url), R.identity)
        )(downloadUrl);
    };

    getDownloadFileName = () => {
        const {
            post: {post_id: postId, content_extension: contentExtension}
        } = this.props;

        const downloadDate = moment().format('YYYYMMDD');

        return `Social-Native-${postId}-${downloadDate}.${contentExtension}`;
    };

    /**
     * @param {Object} scoreResult
     * @memberof ContentModal
     */
    composeContentComment = scoreResult => {
        const {post_user_comment} = scoreResult;
        this.setState({
            transmitting: false,
            composingContentComment: true,
            postUserComment: post_user_comment ? R.head(post_user_comment) : null
        });
    };

    displayCommentComposer = () => {
        const {postUserComment, transmitting} = this.state;

        const {post} = this.props;

        const buttonContent = transmitting ? <LoadingSpinner /> : 'SUBMIT';

        const buttonIsDisabled =
            transmitting || R.isNil(postUserComment) || R.isEmpty(postUserComment);

        return (
            <div className="text-center brand-score-survey">
                <div className="row">
                    <img className="icon-like img img-circle" src={IconLike} />
                </div>

                <div className="row">
                    <h2>Thank You For Scoring This Content!</h2>
                    <StarRating
                        starCount={5}
                        starsFilled={post.post_user_score}
                        clickScore={this.handleClickScore}
                        tooltips={MAPS.ContentScoreDescription}
                        disabled
                    />
                    <h4>Why did you select this rating?</h4>
                </div>

                <div className="row">
                    <textarea
                        className="comment-textarea"
                        value={postUserComment}
                        onChange={this.handleCommentChange}
                    />
                </div>
                <div className="row">
                    <button
                        className="btn btn-success submit-content-comment"
                        onClick={this.handleCommentSubmit}
                        disabled={buttonIsDisabled}
                    >
                        {buttonContent}
                    </button>
                </div>
            </div>
        );
    };

    trackClick = clickName => {
        const {brandName, campaignName, campaignId, email, post} = this.props;
        const creatorUsername = R.path(
            ['content_distribution_metadata', 'snapshots', 'platform_account', 'platform_handle'],
            post
        );
        const contentUrl = R.path(
            ['content_distribution_metadata', 'distribution_platform_post_url'],
            post
        );

        track(email, clickName, {
            'Post Id': post.post_id,
            'Brand Name': brandName,
            'Campaign Funnel Id': campaignId || post.campaign_id,
            'Campaign Funnel Name': campaignName || post.campaign_name,
            'Creator Username': creatorUsername,
            'Content Url': contentUrl || 'No Content Url',
            'Score Given': post.post_user_score ? post.post_user_score : 'Not Scored'
        });
    };

    displayPostScore = () => {
        const {post, showPostScore, showAvgScore} = this.props;
        const {transmitting} = this.state;

        if (showPostScore) {
            const avgScoreSection =
                showAvgScore && post.avg_post_user_score ? `Avg ${post.avg_post_user_score}` : null;

            const content = transmitting ? (
                <LoadingSpinner />
            ) : (
                <StarRating
                    starCount={5}
                    starsFilled={post.post_user_score}
                    clickScore={this.handleClickScore}
                    tooltips={MAPS.ContentScoreDescription}
                />
            );

            return (
                <div className="modal-post-user-score">
                    <hr />

                    <div className="post-user-score-text">Score this content</div>

                    <span className="modal-rating-star-content">{content}</span>

                    <div className="text-center avg-score">{avgScoreSection}</div>

                    <hr />
                </div>
            );
        }

        return null;
    };

    close = () => {
        const {close} = this.props;

        this.setState({
            uploading: false,
            showFacebookPagesSharingForm: false,
            composingContentComment: false,
            adMode: false,
            platform: 'facebook',
            format: 'mobile',
            postUserComment: null
        });

        close();
    };

    displayDownloadButton = () => {
        return (
            <Button
                className="pull-left download-button"
                onClick={this.props.handleDownloadContent}
            >
                <img
                    role="presentation"
                    className="download-icon"
                    // @ts-expect-error
                    src={require('images/icons/icon-download.png')}
                />
                <span>DOWNLOAD</span>
            </Button>
        );
    };

    /**
     * @type {import('react').ChangeEventHandler<HTMLTextAreaElement>}
     * @memberof ContentModal
     */
    handleCommentChange = (e) => {
        this.setState({postUserComment: e.target.value});
    };

    /**
     * @memberof ContentModal
     * @type {import('react').MouseEventHandler<HTMLButtonElement>}
     */
    handleCommentSubmit = e => {
        e.preventDefault();

        const {
            props: {
                post: {content_metadata_id},
                setContentComment
            },
            state: {postUserComment}
        } = this;

        this.setState({transmitting: true}, () => {
            setContentComment(
                {content_metadata_id, text: postUserComment},
                () => {
                    this.setState({
                        composingContentComment: false,
                        postUserComment: null,
                        transmitting: false
                    });
                },
                () => {
                    this.setState({transmitting: false});
                }
            );
        });
    };

    handleClickScore = (e, score) => {
        e.preventDefault();
        e.stopPropagation();

        const {setPostScore, post} = this.props;

        this.setState({transmitting: true}, () => {
            setPostScore(
                score,
                post.post_id,
                this.composeContentComment,
                () => {
                    this.setState({transmitting: false});
                },
                () => {
                    this.setState({transmitting: false});
                }
            );
        });
    };

    handleShareToFacebookAdManager = (url, accountId, accountName) => {
        const {shareToFacebookAdManager} = this.props;

        this.setState(
            {
                uploading: true
            },
            () => {
                shareToFacebookAdManager(url, accountId, accountName, this.close);
            }
        );
    };

    handleShareToTwitterAdManager = (url, accountId, accountName) => {
        const {shareToTwitterAdManager} = this.props;

        this.setState(
            {
                uploading: true
            },
            () => {
                shareToTwitterAdManager(url, accountId, accountName, this.close);
            }
        );
    };

    handleShareToSnapchatAdManager = (url, accountId, accountName) => {
        const {shareToSnapchatAdManager} = this.props;

        this.setState(
            {
                uploading: true
            },
            () => {
                shareToSnapchatAdManager(url, accountId, accountName, this.close);
            }
        );
    };

    handleShareToPinterest = (board_id, {description, link}) => {
        const {post, shareOptions} = this.props;
        const share_url = R.path(['pinterest_ads', 'share_url'], shareOptions);
        const url = R.replace('__POST_ID__', post.post_id, share_url);
        const {boards} = R.path(['shareOptions', 'pinterest_ads'], this.props);
        const pin = {
            board_id,
            // @ts-expect-error
            board_name: R.pipe(R.find(R.propEq('id', board_id)), R.prop('name'))(boards),
            note: description,
            link
        };

        this.setState(
            {
                uploading: true
            },
            () => {
                this.props.shareToPinterest(url, pin, this.close);
            }
        );
    };

    handleCreatePinterestBoard = (board, callback) => {
        const {shareOptions} = this.props;
        const createBoardUrl = shareOptions.pinterest_ads.create_board_url;

        this.setState(
            {
                uploading: true
            },
            () =>
                this.props.createPinterestBoard(createBoardUrl, board, boardId =>
                    this.setState({uploading: false}, () => callback(boardId))
                )
        );
    };

    handleRedirectToContentShareForm = () => {
        this.setState(
            {
                showFacebookPagesSharingForm: true
            },
            () => {
                this.trackClick('FB - Clicked Post to FB');
            }
        );
    };

    handleShareToFacebookPagesAccount = (url, accountId, accountName) => {
        const {shareToFacebookPagesAccount} = this.props;

        const form = this.facebookForm;
        const value = form.getValue();

        if (value) {
            this.setState(
                {
                    uploading: true
                },
                () => {
                    shareToFacebookPagesAccount(url, accountId, accountName, value, this.close);
                }
            );
        }
    };

    hasDesktopOption = platform => R.contains(platform, ['facebook', 'twitter']);

    handleEnterAdMode = platform =>
        this.setState({
            platform,
            adMode: true
        });

    handleExitAdMode = () =>
        this.setState({
            adMode: false,
            platform: 'facebook',
            format: 'mobile'
        });

    handleChangePlatform = platform => {
        const {format} = this.state;

        this.setState({
            platform,
            format: this.hasDesktopOption(platform) ? format : 'mobile'
        });
    };

    handleChangeFormat = format => this.setState({format});

    displayCloseButton = () => {
        const {showDownloadSurvey} = this.props;

        return !showDownloadSurvey ? (
            <div className="modal-close">
                <div className="modal-close-icon" onClick={this.close} />
            </div>
        ) : null;
    };

    displayFacebookAdModeButton = () => {
        const {
            isAdmin,
            isSalesUser,
            shareOptions,
            post: {post_id: postId}
        } = this.props;

        const {uploading} = this.state;

        const facebookPagesShareOptions = R.prop('facebook_pages', shareOptions);

        return (
            <div className="facebook-controls">
                <Button bsStyle="primary" onClick={() => this.handleEnterAdMode('facebook')}>
                    <div className="facebook-logo-icon" />
                    <span className="facebook-button-text facebook-ad-text">
                        Run as Facebook Ad
                    </span>
                </Button>
                <FacebookPagesShareButton
                    redirect_to_modal
                    disabled={uploading}
                    post_id={postId}
                    share_options={facebookPagesShareOptions}
                    isSalesUser={isSalesUser}
                    isAdmin={isAdmin}
                    on_share={this.handleRedirectToContentShareForm}
                />
            </div>
        );
    };

    displayFacebookPageSharingForm = () => {
        const FacebookPagesShare = t.struct({
            caption: t.Str
        });

        const facebookPagesShareTemplate = locals => (
            <div className="caption-box">{locals.inputs.caption}</div>
        );

        const facebookPagesShareContentOptions = {
            template: facebookPagesShareTemplate,
            fields: {
                caption: {
                    label: <FormLabel>Caption</FormLabel>,
                    type: 'textarea',
                    attrs: {
                        placeholder: 'Please Enter a Caption'
                    }
                }
            }
        };

        const {
            shareOptions,
            post: {post_id: postId}
        } = this.props;
        const {uploading, showFacebookPagesSharingForm} = this.state;

        if (!showFacebookPagesSharingForm) {
            return null;
        }

        const facebookPagesShareOptions = R.prop('facebook_pages', shareOptions);

        return (
            <div className="facebook-page-sharing-form">
                <Form
                    ref={form => {
                        this.facebookForm = form;
                    }}
                    options={facebookPagesShareContentOptions}
                    type={FacebookPagesShare}
                    value={{caption: ''}}
                />
                <div className="facebook-controls">
                    <FacebookPagesShareButton
                        disabled={uploading}
                        post_id={postId}
                        share_options={facebookPagesShareOptions}
                        redirect_to_modal={false}
                        on_share={this.handleShareToFacebookPagesAccount}
                    />
                </div>
            </div>
        );
    };

    displaySnapchatAdModeButton = () => (
        <div className="snapchat-controls">
            <Button bsStyle="primary" onClick={() => this.handleEnterAdMode('snapchat')}>
                <div className="snapchat-logo-icon" />
                <span className="snapchat-button-text snapchat-ad-text">Run as Snapchat Ad</span>
            </Button>
        </div>
    );

    displayInstagramAdModeButton = () => (
        <div className="instagram-controls">
            <Button bsStyle="success" onClick={() => this.handleEnterAdMode('instagram')}>
                <div className="instagram-logo-icon" />
                <span className="instagram-button-text instagram-ad-text">Run as Instagram Ad</span>
            </Button>
        </div>
    );

    displayTwitterAdModeButton = () => (
        <div className="twitter-controls">
            <Button bsStyle="success" onClick={() => this.handleEnterAdMode('twitter')}>
                <div className="twitter-logo-icon" />
                <span className="twitter-button-text twitter-ad-text">Run as Twitter Ad</span>
            </Button>
        </div>
    );

    displayEngagementsSection = () => {
        const {content_distribution_metadata: contentDistributionMetadata} = this.props.post;

        if (contentDistributionMetadata) {
            const likes = R.path(['snapshots', 'social_engagements', 'likes'])(
                contentDistributionMetadata
            );
            const replies = R.path(['snapshots', 'social_engagements', 'replies'])(
                contentDistributionMetadata
            );

            return (
                <div className="engagements">
                    <div className="likes">
                        <i className="fa fa-heart-o" />
                        <div>{likes}</div>
                    </div>
                    <div className="comments">
                        <i className="fa fa-comment-o" />
                        <div>{replies}</div>
                    </div>
                </div>
            );
        }

        return null;
    };

    getUsername = () => {
        const {
            content_distribution_metadata: contentDistributionMetadata,
            username,
            tiktok_username
        } = this.props.post;
        const {campaignType} = this.props;

        let usernameToDisplay = username;
        let isTikTok = campaignType === 'tiktok';

        // If content is distributed, check distribution metadata to check distribution platform
        if (contentDistributionMetadata) {
            const postUrl = R.path(['distribution_platform_post_url'], contentDistributionMetadata)
                || '';

            isTikTok = postUrl.includes('tiktok.com');

            usernameToDisplay = ObjectHelper.pathOr(
                username,
                ['snapshots', 'platform_account', 'platform_handle'],
                contentDistributionMetadata
            );
        }

        // If post is distributed to TikTok
        // or content is not yet distributed and a campaign is TikTok
        // then display TikTok username
        if (isTikTok && tiktok_username) {
            return tiktok_username;
        }

        return usernameToDisplay;
    };

    displayCreatorInfoSection = () => {
        const {
            content_distribution_metadata: contentDistributionMetadata,
            profile_image_url: profileImageUrl,
            caption: fallbackCaption
        } = this.props.post;

        const username = this.getUsername();

        if (contentDistributionMetadata) {
            let caption = R.path(['caption'])(contentDistributionMetadata);

            const {distribution_platform: distributionPlatform} = contentDistributionMetadata;

            if (distributionPlatform && distributionPlatform === 'twitter') {
                const elem = document.createElement('textarea');
                elem.innerHTML = caption;
                caption = elem.value;
            }

            const postUrl = R.path(['distribution_platform_post_url'], contentDistributionMetadata);

            return (
                <div>
                    <div className="creator-info">
                        <div className="img-username">
                            <img role="presentation" className="avatar-img" src={profileImageUrl} />
                            <a href={postUrl} target="_blank" rel="noopener noreferrer">
                                {username}
                            </a>
                        </div>

                        <hr />

                        <div className="username-caption">
                            <span className="caption">{caption}</span>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div>
                <div className="creator-info">
                    <div className="img-username">
                        <span>{username}</span>
                    </div>

                    <hr />

                    <div className="username-caption">
                        <span className="caption">{fallbackCaption}</span>
                    </div>
                </div>
            </div>
        );
    };

    displayAdModeContent = () => {
        const {post} = this.props;
        const {platform, format} = this.state;

        if (R.equals(platform, ENUMS.CampaignType.SNAPCHAT)) {
            return <OverlayPreview platform={platform} format={format} url={post.thumbnail_url} />;
        }

        if (R.equals(platform, ENUMS.CampaignType.PINTEREST)) {
            return <SandwichPreview platform={platform} format={format} url={post.thumbnail_url} />;
        }

        return <CutoutPreview platform={platform} format={format} url={post.thumbnail_url} />;
    };

    displayAdModeInfo = () => {
        const {platform, format, uploading} = this.state;

        const props = {
            ...this.props,
            platform,
            format,
            uploading,
            activePlatforms: ImageModal.activePlatforms,
            handleExitAdMode: this.handleExitAdMode,
            handleChangePlatform: this.handleChangePlatform,
            handleChangeFormat: this.handleChangeFormat,
            hasDesktopOption: this.hasDesktopOption,
            trackClick: this.trackClick,
            handleShareToFacebookAdManager: this.handleShareToFacebookAdManager,
            handleShareToTwitterAdManager: this.handleShareToTwitterAdManager,
            handleShareToSnapchatAdManager: this.handleShareToSnapchatAdManager,
            handleShareToPinterest: this.handleShareToPinterest,
            handleCreatePinterestBoard: this.handleCreatePinterestBoard
        };

        return <AdModeInfo {...props} />;
    };

    render() {
        const {show} = this.props;

        const closeButton = this.displayCloseButton();
        const modalBodyContent = this.displayModalBodyContent();
        const modalBodyInfo = this.displayModalBodyInfo();

        return (
            <Modal show={show} onHide={this.close} className="image-modal">
                <Modal.Body>
                    <div className="image-modal-body">
                        <div className="image-modal-body-img">{modalBodyContent}</div>

                        <div className="image-modal-body-info">
                            {closeButton}
                            {modalBodyInfo}
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        );
    }
}



// WEBPACK FOOTER //
// ./campaign_analytics/v2/campaign_analytics_page/posts/image_modal/index.js